import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Icon } from '../../components/icons/Icon'
import { InlineAlert } from '../../components/InlineAlert'
import { AccountLookupField } from '../accounts/AccountLookupField'
import { LegalEntityLookupField } from '../branches/LegalEntityLookupField'
import {
  createCase,
  declareSubmissionScope,
  determineProgramApplicability,
  overrideProgramApplicability,
} from '../../api/cases'
import { listProductionsByLegalEntity } from '../../api/film'
import { listPrograms, type ProgramView } from '../../api/programs'
import { ApiError } from '../../api/problem'
import type { ProgramApplicabilityView } from '../../types/case'
import type { ProductionView } from '../../types/film'

type EntityRef = { id: string; name: string }
type IncludeFederal = 'yes' | 'no'
type StateMode = 'none' | 'all_applicable' | 'specific'

const STEPS = ['Account & legal entity', 'Production', 'Submission scope', 'Review & create'] as const

/**
 * IB-326 — the in-app "New Case" wizard: `/cases/new`, Film & Media only for this story
 * (docs/design/remaining-scope-designs.md §IB-326). Collects Account → Legal entity → Production
 * → SubmissionScope election, then makes exactly two writes — `createCase` (AC1) and
 * `declareSubmissionScope` (AC2, which materializes the Case's first `ProgramFiling`(s) in the
 * same backend transaction) — before landing on the new Case's detail page. Every entity reference
 * is chosen through a typeahead/lookup (AccountLookupField/LegalEntityLookupField/a Production
 * picker) — no raw-UUID input anywhere. No guarded field (`qre_pct`/`qualifying_pct`/in-state/
 * credit) is written by this screen; those stay on the `AdjustmentService` path exclusively.
 */
export function NewCaseScreen() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const initialLegalEntityId = searchParams.get('legalEntityId')

  const [step, setStep] = useState(0)
  const [account, setAccount] = useState<EntityRef | null>(null)
  const [legalEntity, setLegalEntity] = useState<EntityRef | null>(
    initialLegalEntityId ? { id: initialLegalEntityId, name: initialLegalEntityId } : null,
  )

  const [productions, setProductions] = useState<ProductionView[]>([])
  const [productionsLoading, setProductionsLoading] = useState(false)
  const [productionId, setProductionId] = useState<string | null>(null)

  const [includeFederal, setIncludeFederal] = useState<IncludeFederal>('no')
  const [stateMode, setStateMode] = useState<StateMode>('none')
  const [applicability, setApplicability] = useState<ProgramApplicabilityView[]>([])
  const [statePrograms, setStatePrograms] = useState<Map<string, ProgramView>>(new Map())
  const [selectedProgramIds, setSelectedProgramIds] = useState<Set<string>>(new Set())
  const [scopeLoading, setScopeLoading] = useState(false)
  const [scopeError, setScopeError] = useState<string | null>(null)

  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Step 2 — the Legal entity's own Productions (film Cases require an existing productionId).
  useEffect(() => {
    if (step !== 1 || !legalEntity) return
    setProductionsLoading(true)
    listProductionsByLegalEntity(legalEntity.id)
      .then(setProductions)
      .catch(() => setProductions([]))
      .finally(() => setProductionsLoading(false))
  }, [step, legalEntity])

  // Step 3 — footprint-determine + the film state-level Program catalogue, feeding the
  // "all_applicable"/"specific" state-mode picker and the manual-override affordance (AC2).
  useEffect(() => {
    if (step !== 2 || !legalEntity || stateMode === 'none') return
    setScopeLoading(true)
    setScopeError(null)
    Promise.all([
      determineProgramApplicability(legalEntity.id, 'film'),
      listPrograms({ filter: 'kind==film;level=in=(state,provincial)', limit: 200 }),
    ])
      .then(([rows, page]) => {
        setApplicability(rows)
        setStatePrograms(new Map(page.items.map((p) => [p.id, p])))
      })
      .catch((err) => {
        setScopeError(err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not load program applicability.')
      })
      .finally(() => setScopeLoading(false))
  }, [step, legalEntity, stateMode])

  async function handleOverride(row: ProgramApplicabilityView) {
    setScopeError(null)
    try {
      const updated = await overrideProgramApplicability(row.id, { applicable: true })
      setApplicability((prev) => prev.map((r) => (r.id === updated.id ? updated : r)))
    } catch (err) {
      setScopeError(err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not override this program.')
    }
  }

  function toggleSelectedProgram(programId: string) {
    setSelectedProgramIds((prev) => {
      const next = new Set(prev)
      if (next.has(programId)) next.delete(programId)
      else next.add(programId)
      return next
    })
  }

  const applicablePrograms = applicability.filter((row) => row.applicable && statePrograms.has(row.programId))
  const notApplicablePrograms = applicability.filter((row) => !row.applicable && statePrograms.has(row.programId))

  const canAdvanceFromStep0 = Boolean(account && legalEntity)
  const canAdvanceFromStep1 = Boolean(productionId)
  const canAdvanceFromStep2 = stateMode !== 'specific' || selectedProgramIds.size > 0
  const canCreate = canAdvanceFromStep0 && canAdvanceFromStep1 && canAdvanceFromStep2

  async function handleCreate() {
    if (!account || !legalEntity || !productionId || submitting) return
    setSubmitting(true)
    setSubmitError(null)
    try {
      const created = await createCase({
        accountId: account.id,
        legalEntityId: legalEntity.id,
        kind: 'film',
        productionId,
      })
      await declareSubmissionScope(created.id, {
        includeFederal,
        stateMode,
        selectedProgramIds: stateMode === 'specific' ? Array.from(selectedProgramIds) : null,
      })
      navigate(`/cases/${created.id}`)
    } catch (err) {
      setSubmitError(err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not create this case.')
      setSubmitting(false)
    }
  }

  return (
    <div style={{ padding: 'var(--iw-space-5)', maxWidth: 640, display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-3)' }}>
        <span style={{ color: 'var(--iw-accent)' }}>
          <Icon name="briefcase" size={20} />
        </span>
        <h1 style={{ margin: 0, fontSize: 'var(--iw-size-lg)' }}>New case — Film &amp; Media</h1>
      </div>

      <ol
        aria-label="Wizard steps"
        style={{ display: 'flex', gap: 'var(--iw-space-3)', listStyle: 'none', margin: 0, padding: 0, fontSize: 'var(--iw-size-xs)' }}
      >
        {STEPS.map((label, index) => (
          <li
            key={label}
            aria-current={index === step ? 'step' : undefined}
            style={{
              color: index === step ? 'var(--iw-accent)' : 'var(--iw-text-muted)',
              fontWeight: index === step ? ('var(--iw-weight-medium)' as never) : undefined,
            }}
          >
            {index + 1}. {label}
          </li>
        ))}
      </ol>

      {submitError ? <InlineAlert severity="error">{submitError}</InlineAlert> : null}

      {step === 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}>
          <AccountLookupField
            id="new-case-account"
            label="Account"
            value={account}
            onChange={(next) => {
              setAccount(next)
              setLegalEntity(null)
            }}
          />
          <LegalEntityLookupField
            id="new-case-legal-entity"
            label="Legal entity"
            value={legalEntity}
            onChange={setLegalEntity}
            accountId={account?.id}
          />
        </div>
      ) : null}

      {step === 1 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-3)' }}>
          <p style={{ margin: 0, color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-sm)' }}>
            A Film &amp; Media case attaches to exactly one Production.
          </p>
          {productionsLoading ? (
            <p style={{ color: 'var(--iw-text-muted)' }}>Loading productions…</p>
          ) : productions.length === 0 ? (
            <InlineAlert severity="info">
              No productions found for this legal entity yet.{' '}
              <Link to={legalEntity ? `/productions/new?legalEntityId=${legalEntity.id}` : '/productions/new'}>
                Create one
              </Link>
              , then come back to this step.
            </InlineAlert>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-2)' }}>
              {productions.map((production) => (
                <li key={production.id}>
                  <button
                    type="button"
                    className={`iw-btn ${productionId === production.id ? 'iw-btn--primary' : ''}`}
                    style={{ width: '100%', justifyContent: 'flex-start' }}
                    onClick={() => setProductionId(production.id)}
                    aria-pressed={productionId === production.id}
                  >
                    <Icon name="film" size={14} /> {production.title} ({production.type})
                  </button>
                </li>
              ))}
            </ul>
          )}
          <Link to={legalEntity ? `/productions/new?legalEntityId=${legalEntity.id}` : '/productions/new'} className="iw-btn iw-btn--sm" style={{ alignSelf: 'flex-start' }}>
            <Icon name="plus" size={12} /> New production
          </Link>
        </div>
      ) : null}

      {step === 2 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}>
          <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
            <legend className="iw-field-label">Include the federal program?</legend>
            <label style={{ display: 'block', marginTop: 4 }}>
              <input type="radio" name="includeFederal" checked={includeFederal === 'yes'} onChange={() => setIncludeFederal('yes')} /> Yes
            </label>
            <label style={{ display: 'block' }}>
              <input type="radio" name="includeFederal" checked={includeFederal === 'no'} onChange={() => setIncludeFederal('no')} /> No
            </label>
          </fieldset>

          <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
            <legend className="iw-field-label">State/provincial programs</legend>
            <label style={{ display: 'block', marginTop: 4 }}>
              <input type="radio" name="stateMode" checked={stateMode === 'none'} onChange={() => setStateMode('none')} /> None
            </label>
            <label style={{ display: 'block' }}>
              <input type="radio" name="stateMode" checked={stateMode === 'all_applicable'} onChange={() => setStateMode('all_applicable')} /> All applicable (auto-determined from footprint)
            </label>
            <label style={{ display: 'block' }}>
              <input type="radio" name="stateMode" checked={stateMode === 'specific'} onChange={() => setStateMode('specific')} /> Specific programs
            </label>
          </fieldset>

          {stateMode !== 'none' ? (
            scopeLoading ? (
              <p style={{ color: 'var(--iw-text-muted)' }}>Determining program applicability…</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-2)' }}>
                {scopeError ? <InlineAlert severity="error">{scopeError}</InlineAlert> : null}
                {applicablePrograms.length === 0 ? (
                  <p style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-sm)' }}>
                    No state/provincial programs are applicable from the current footprint.
                  </p>
                ) : (
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {applicablePrograms.map((row) => {
                      const program = statePrograms.get(row.programId)!
                      return (
                        <li key={row.id}>
                          {stateMode === 'specific' ? (
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <input
                                type="checkbox"
                                checked={selectedProgramIds.has(row.programId)}
                                onChange={() => toggleSelectedProgram(row.programId)}
                              />
                              {program.jurisdiction} — {program.code}
                            </label>
                          ) : (
                            <span>
                              {program.jurisdiction} — {program.code}
                            </span>
                          )}
                        </li>
                      )
                    })}
                  </ul>
                )}
                {notApplicablePrograms.length > 0 ? (
                  <details>
                    <summary style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)', cursor: 'pointer' }}>
                      Not applicable from footprint — override manually
                    </summary>
                    <ul style={{ listStyle: 'none', margin: 'var(--iw-space-2) 0 0', padding: 0 }}>
                      {notApplicablePrograms.map((row) => {
                        const program = statePrograms.get(row.programId)!
                        return (
                          <li key={row.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-sm)' }}>
                              {program.jurisdiction} — {program.code}
                            </span>
                            <button type="button" className="iw-btn iw-btn--sm" onClick={() => void handleOverride(row)}>
                              Override as applicable
                            </button>
                          </li>
                        )
                      })}
                    </ul>
                  </details>
                ) : null}
              </div>
            )
          ) : null}
        </div>
      ) : null}

      {step === 3 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-2)', fontSize: 'var(--iw-size-sm)' }}>
          <div>
            <strong>Account:</strong> {account?.name}
          </div>
          <div>
            <strong>Legal entity:</strong> {legalEntity?.name}
          </div>
          <div>
            <strong>Production:</strong> {productions.find((p) => p.id === productionId)?.title ?? productionId}
          </div>
          <div>
            <strong>Federal:</strong> {includeFederal === 'yes' ? 'Included' : 'Not included'}
          </div>
          <div>
            <strong>State/provincial:</strong>{' '}
            {stateMode === 'none' ? 'None' : stateMode === 'all_applicable' ? 'All applicable' : `${selectedProgramIds.size} selected`}
          </div>
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
        <div>
          {step > 0 ? (
            <button type="button" className="iw-btn" onClick={() => setStep((s) => s - 1)}>
              Back
            </button>
          ) : (
            <Link to="/cases" className="iw-btn">
              Cancel
            </Link>
          )}
        </div>
        <div>
          {step < STEPS.length - 1 ? (
            <button
              type="button"
              className="iw-btn iw-btn--primary"
              disabled={(step === 0 && !canAdvanceFromStep0) || (step === 1 && !canAdvanceFromStep1) || (step === 2 && !canAdvanceFromStep2)}
              onClick={() => setStep((s) => s + 1)}
            >
              Next
            </button>
          ) : (
            <button type="button" className="iw-btn iw-btn--primary" disabled={!canCreate || submitting} onClick={() => void handleCreate()}>
              {submitting ? 'Creating…' : 'Create case'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
